import { useState } from "react";
import { AlertTriangle, ArrowDownWideNarrow, CalendarCheck, CalendarDays, ChevronDown, Inbox, Plus, Repeat, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { prettyDate } from "@/lib/dates";
import { isManual, sortTasks } from "@/lib/order";
import { TaskCard } from "./TaskCard";
import { SortableList } from "./SortableList";
import { EmployeeChip } from "./EmployeeBadge";
import { usualSummary } from "./RecurringHabits";

const LateList = ({ tasks, empById, h, open, onToggle }) => (
  <div data-testid="late-section" className="mt-4 space-y-2">
    <SectionHead
      label="In ritardo da riprogrammare"
      testId="late"
      open={open}
      onToggle={onToggle}
      className="text-rose-600"
      right={<span data-testid="late-count" className="count-pill ml-auto" style={{ background: "#e11d48" }}>{tasks.length}</span>}
    >
      <AlertTriangle className="h-3.5 w-3.5" />
    </SectionHead>
    {open && tasks.map((t) => (
      <TaskCard
        key={t.id}
        task={t}
        h={h}
        showAssign
        assignLabel="Riprogramma"
        idPrefix="late-"
        meta={
          <>
            <span className="inline-flex items-center gap-0.5"><CalendarDays className="h-2.5 w-2.5" />{prettyDate(t.date)}</span>
            {empById[t.employee_id] && <EmployeeChip employee={empById[t.employee_id]} />}
          </>
        }
      />
    ))}
  </div>
);

const SectionHead = ({ label, count, tasks = [], h, testId, open, onToggle, right, className = "text-slate-500", children }) => (
  <div className="mt-1 flex items-center gap-1.5">
    <button
      type="button"
      data-testid={`${testId}-toggle`}
      aria-expanded={open}
      onClick={onToggle}
      title={open ? "Nascondi" : "Mostra"}
      className={`-ml-1 flex min-h-[32px] items-center gap-1.5 rounded-md px-1 text-left text-[11px] font-semibold uppercase tracking-wider transition-colors hover:bg-slate-100/70 ${className}`}
    >
      <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
      {children}
      {label}
      {count != null && <span className="font-medium normal-case tracking-normal opacity-70">({count})</span>}
    </button>
    {open && isManual(tasks) && (
      <button type="button" data-testid={`${testId}-sort-btn`} onClick={() => h.resetOrder(tasks)} className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100">
        <ArrowDownWideNarrow className="h-3 w-3" /> Ordina per priorità
      </button>
    )}
    {right}
  </div>
);

const UsualMeta = ({ task, empById }) => {
  const text = usualSummary(task, empById);
  return (
    <span data-testid={`task-usual-${task.id}`} className="inline-flex items-center gap-0.5 rounded bg-violet-100/70 px-1 font-medium text-violet-800">
      <CalendarCheck className="h-2.5 w-2.5" /> {text || "Nessun abituale"}
    </span>
  );
};

const WeeklyToggle = ({ task, empById, h }) => {
  const ready = !!usualSummary(task, empById);
  const on = ready && task.auto_weekly;
  return (
    <button
      type="button"
      data-testid={`task-weekly-${task.id}`}
      aria-pressed={on}
      onClick={() => (ready ? h.toggleWeekly(task) : h.edit(task))}
      className={`mt-2 flex min-h-[36px] w-full items-center gap-2 rounded-lg px-2.5 text-left text-xs font-semibold ring-1 transition-colors ${on ? "bg-emerald-50 text-emerald-800 ring-emerald-200" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}
    >
      <span className={`relative h-4 w-7 shrink-0 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${on ? "translate-x-3.5" : "translate-x-0.5"}`} />
      </span>
      {ready ? "Ripeti ogni settimana" : "Scegli dipendenti e giorni per ripeterla"}
    </button>
  );
};

const RecurringList = ({ tasks, empById, h, open, onToggle }) => (
  <div data-testid="recurring-section" className="mt-4 flex flex-col gap-2 rounded-xl bg-violet-50/60 p-2.5 ring-1 ring-violet-100">
    <SectionHead label="Ricorrenti" count={tasks.length} tasks={tasks} h={h} testId="recurring" open={open} onToggle={onToggle} className="text-violet-700">
      <Repeat className="h-3.5 w-3.5" />
    </SectionHead>
    {open && (
      <>
        <p className="-mt-1 text-[11px] text-slate-500">Restano qui: ogni volta che le assegni, nella griglia va una copia. Accendi "Ripeti ogni settimana" su quelle da mettere in automatico in tutte le settimane.</p>
        <SortableList tasks={tasks} h={h} cardProps={(t) => ({ showAssign: true, meta: <UsualMeta task={t} empById={empById} />, footer: <WeeklyToggle task={t} empById={empById} h={h} /> })} />
        <button type="button" data-testid="recurring-new-btn" onClick={() => h.add(null, null, { recurring: true })} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-violet-300 py-2 text-xs font-semibold text-violet-700 transition-colors hover:bg-white">
          <Plus className="h-3.5 w-3.5" /> Nuova ricorrente
        </button>
      </>
    )}
  </div>
);

const MovedFrom = ({ task, empById }) => (
  <span data-testid={`task-moved-from-${task.id}`} className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1 font-medium text-amber-800">
    <UserX className="h-2.5 w-2.5" />
    {empById[task.moved_from.employee_id]?.name || "Dipendente"} assente {prettyDate(task.moved_from.date)}
  </span>
);

export const UnassignedPanel = ({ tasks, late, recurring, employees, h }) => {
  const [over, setOver] = useState(false);
  const [closed, setClosed] = useState({});
  const toggle = (key) => () => setClosed((c) => ({ ...c, [key]: !c[key] }));
  const open = sortTasks(tasks.filter((t) => !t.completed));
  const done = tasks.filter((t) => t.completed);
  const rec = sortTasks(recurring);
  const empById = Object.fromEntries(employees.map((e) => [e.id, e]));
  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    const id = e.dataTransfer.getData("text/plain");
    if (id && !e.nativeEvent.reorderHandled) h.move(id, null, null);
  };

  return (
    <div
      data-testid="unassigned-panel"
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={`unassigned-panel ${over ? "is-over" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-base font-semibold">Da assegnare</h2>
          <p className="text-xs text-slate-500">Trascina o tocca "Assegna"</p>
        </div>
        <span data-testid="unassigned-count" className="count-pill">{open.length}</span>
      </div>
      <Button data-testid="unassigned-new-btn" variant="outline" className="mt-3 w-full justify-start gap-2 rounded-xl border-dashed" onClick={() => h.add(null, null)}>
        <Plus className="h-4 w-4" /> Nuova attività da assegnare
      </Button>
      {late.length > 0 && <LateList tasks={late} empById={empById} h={h} open={!closed.late} onToggle={toggle("late")} />}
      <RecurringList tasks={rec} empById={empById} h={h} open={!closed.recurring} onToggle={toggle("recurring")} />
      <div className="mt-4 flex flex-col gap-2">
        <SectionHead label="Non assegnate" count={open.length} tasks={open} h={h} testId="unassigned" open={!closed.open} onToggle={toggle("open")} />
        {!closed.open && (
          <>
            <SortableList tasks={open} h={h} cardProps={(t) => ({ showAssign: true, meta: t.moved_from && <MovedFrom task={t} empById={empById} /> })} />
            {open.length === 0 && (
              <div data-testid="unassigned-empty" className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
                <Inbox className="h-5 w-5" /> Nessuna attività in attesa
              </div>
            )}
          </>
        )}
        {done.length > 0 && (
          <div data-testid="done-section" className="mt-2 flex flex-col gap-2">
            <SectionHead label="Completate" count={done.length} testId="done" open={!closed.done} onToggle={toggle("done")} className="text-slate-400" />
            {!closed.done && done.map((t) => <TaskCard key={t.id} task={t} h={h} />)}
          </div>
        )}
      </div>
    </div>
  );
};
